/**
 * Crawler and Bot Detection Logger
 * Logs visits from search engine crawlers and AI agents
 * 
 * Tracked bots:
 * - Googlebot, Bingbot, Slurp (Yahoo)
 * - Social: Twitterbot, facebookexternalhit, LinkedInBot
 * - AI: GPTBot, Claude-Web, anthropic-ai, CCBot, Google-Extended
 */

interface CrawlerLog {
  userAgent: string;
  botType: string;
  url: string;
  timestamp: number;
  statusCode?: number;
}

const KNOWN_BOTS: Record<string, string> = {
  'Googlebot': 'search',
  'Bingbot': 'search',
  'Slurp': 'search',
  'DuckDuckBot': 'search',
  'Baiduspider': 'search',
  'YandexBot': 'search',
  'Twitterbot': 'social',
  'facebookexternalhit': 'social',
  'LinkedInBot': 'social',
  'WhatsApp': 'social',
  'GPTBot': 'ai',
  'ChatGPT-User': 'ai',
  'Claude-Web': 'ai',
  'anthropic-ai': 'ai',
  'CCBot': 'ai',
  'Google-Extended': 'ai',
  'PerplexityBot': 'ai',
  'YouBot': 'ai'
};

// Detect if user agent is a bot
export const detectBot = (userAgent: string): { isBot: boolean; botName: string; botType: string } => {
  for (const [botName, botType] of Object.entries(KNOWN_BOTS)) {
    if (userAgent.includes(botName)) {
      return { isBot: true, botName, botType };
    }
  }
  return { isBot: false, botName: '', botType: '' };
};

// Log crawler visit
export const logCrawlerVisit = (userAgent?: string) => {
  const ua = userAgent || navigator.userAgent;
  const botInfo = detectBot(ua);
  
  if (!botInfo.isBot) return;

  const log: CrawlerLog = {
    userAgent: ua,
    botType: botInfo.botName,
    url: window.location.href,
    timestamp: Date.now()
  };

  console.log('[Crawler]', log);

  // Store in localStorage for client-side stats (last 100 entries)
  try {
    const logs = JSON.parse(localStorage.getItem('crawler_logs') || '[]');
    logs.push(log);
    if (logs.length > 100) logs.shift();
    localStorage.setItem('crawler_logs', JSON.stringify(logs));
  } catch (e) {
    console.error('Failed to store crawler log:', e);
  }

  // TODO: Send to backend analytics endpoint
  /*
  fetch('/api/analytics/crawler', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(log)
  }).catch(err => console.error('Failed to log crawler visit:', err));
  */
};

// Get crawler stats from last 7 days
export const getCrawlerStats = (days: number = 7) => {
  try {
    const logs: CrawlerLog[] = JSON.parse(localStorage.getItem('crawler_logs') || '[]');
    const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
    const recentLogs = logs.filter(log => log.timestamp > cutoff);

    const stats = {
      total: recentLogs.length,
      byBot: {} as Record<string, number>,
      byType: {} as Record<string, number>,
      urls: {} as Record<string, number>
    };

    recentLogs.forEach(log => {
      stats.byBot[log.botType] = (stats.byBot[log.botType] || 0) + 1;
      
      const botInfo = detectBot(log.userAgent);
      stats.byType[botInfo.botType] = (stats.byType[botInfo.botType] || 0) + 1;
      
      const path = new URL(log.url).pathname;
      stats.urls[path] = (stats.urls[path] || 0) + 1;
    });

    return stats;
  } catch (e) {
    console.error('Failed to get crawler stats:', e);
    return null;
  }
};

// Initialize crawler logging on page load
export const initCrawlerLogging = () => {
  logCrawlerVisit();
  
  // Log on navigation changes
  window.addEventListener('popstate', () => logCrawlerVisit());
};
